






















































import { computed, defineComponent, toRef } from "@vue/composition-api";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import useRole from "@/hooks/useRole";
import emitter from "@/lib/emitter";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getRecentlyActiveTime } from "@/lib/time";
import config from "@/config";
import { EVENTS } from "@/const";
import BaseIcon from "@/components/BaseIcon";
import SubscriberIcon from "@/views/PageMessageThreads/components/SubscriberIcon.vue";
import AvatarBorderAnimation from "./components/AvatarBorderAnimation";

enum SIZE {
  BIG = "BIG",
  MEDIUM = "MEDIUM",
  SMALL = "SMALL",
}

export default defineComponent({
  name: "BaseAvatar",
  components: { AvatarBorderAnimation, SubscriberIcon, BaseIcon },
  props: {
    size: { type: [Number, String], default: 112 },
    user: { type: Object as () => UserPublicData | User | null },
    hideOnlineStatus: { type: Boolean, default: false },
    hideBroadcasting: { type: Boolean, default: false },
    animate: { type: Boolean, default: false },
    showBlockStatus: { type: Boolean, default: false },
    showStoryCreateBtn: { type: Boolean, default: false },
    showStoryInfo: { type: Boolean, default: false },
    forceImageUrl: { type: String },
  },
  setup(props) {
    const { authUser } = useAuthUser();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const { isCreator } = useRole(toRef(props, "user"));
    const src = computed(() =>
      props.forceImageUrl
        ? props.forceImageUrl
        : (props.user && props.user.avatar && MediaFilesController.getImageThumbnail(props.user.avatar.urlPart, 224)) ||
          ""
    );
    const isAuthor = computed(() => authUser.value?.id === props.user?.id);
    const invisible = computed(
      () => !!props.user && "onlineStatus" in props.user && !!props.user.onlineStatus?.invisible && isAuthor.value
    );
    const blurred = computed(
      () =>
        !isAuthor.value &&
        isCreator.value &&
        !!props.user &&
        "nsfwSettings" in props.user &&
        !props.user.nsfwSettings?.safeForWork &&
        !showNotSafeForWork.value
    );
    const isUserStreaming = computed(
      () =>
        props.user &&
        "publicLivechatUser" in props.user &&
        !!props.user.publicLivechatUser &&
        props.user.onlineStatus &&
        !props.hideBroadcasting &&
        !props.user.onlineStatus.invisible
    );

    const online = computed(
      () =>
        (config.featureUserOnlineStatus &&
          props.user &&
          "onlineStatus" in props.user &&
          props.user.onlineStatus &&
          props.user.onlineStatus.isOnline &&
          !props.user.onlineStatus.invisible &&
          !isUserStreaming.value &&
          !props.hideOnlineStatus) ||
        false
    );

    const isRecentlyActive = computed(
      () =>
        (config.featureUserOnlineStatus &&
          props.user &&
          "onlineStatus" in props.user &&
          props.user.onlineStatus &&
          !props.user.onlineStatus.isOnline &&
          new Date(props.user.onlineStatus.lastOnlineTime) >= getRecentlyActiveTime() &&
          !props.hideOnlineStatus) ||
        false
    );

    const isPrivateLiveShow = computed(
      () =>
        isUserStreaming.value &&
        props.user &&
        "publicLivechatUser" in props.user &&
        props.user?.publicLivechatUser?.broadcasting === false &&
        // on "Create Live" backend send notification before changing broadcasting status
        // https://gitlab.com/webvideo/webentertainment/frontend-client/-/merge_requests/8708#note_2708376789
        props.user?.publicLivechatUser?.joinTime !== props.user?.publicLivechatUser?.lastOnline
    );
    const hasStories = computed(
      () =>
        config.featureStories &&
        props.showStoryInfo &&
        props.user &&
        "hasStories" in props.user &&
        props.user.hasStories
    );
    const hasNewStories = computed(
      () =>
        config.featureStories &&
        props.showStoryInfo &&
        props.user &&
        "firstNotViewedStoryId" in props.user &&
        !!props.user.firstNotViewedStoryId
    );
    const sizeDimension = computed(() => (typeof props.size === "string" ? props.size : `${props.size}px`));
    const sizeNumber = computed(() => parseInt(sizeDimension.value));
    const liveBorderWidth = computed(() => (sizeNumber.value > 100 ? 4 : sizeNumber.value > 50 ? 3 : 2));
    const elementSize = computed<SIZE>(() =>
      sizeNumber.value >= 100 ? SIZE.BIG : sizeNumber.value >= 50 ? SIZE.MEDIUM : SIZE.SMALL
    );
    const borderWidth = computed(() =>
      elementSize.value === SIZE.BIG ? 4 : elementSize.value === SIZE.MEDIUM ? 3 : 2
    );
    const subscriberCost = computed(() =>
      props.user && "subscriptionToMe" in props.user ? props.user.subscriptionToMe?.amount ?? 0 : 0
    );

    const createStory = () =>
      emitter.emit(EVENTS.SHOW_CREATE_POST, { userId: props.user?.id, type: ContentFeedTypeEnum.STORY });

    return {
      src,
      online,
      isUserStreaming,
      isPrivateLiveShow,
      sizeDimension,
      sizeNumber,
      liveBorderWidth,
      subscriberCost,
      invisible,
      elementSize,
      borderWidth,
      blurred,
      createStory,
      hasStories,
      hasNewStories,
      SIZE,
      isRecentlyActive,
    };
  },
});
